import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading } from "../components/Layout";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, KeyRound, BookOpen, Mail, Plus, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";
import CurrencySettings from "../components/CurrencySettings";
import DncList from "../components/DncList";
import { STAGES, ROLE_LABEL } from "../lib/constants";

const REPORT_CADENCES = ["daily", "weekly", "monthly"];
const REPORT_LEVELS = ["summary", "detailed"];
const TAB_DEFS = [
  { key: "workspace", label: "Workspace" },
  { key: "pipeline", label: "Pipeline & Approvals" },
  { key: "email", label: "Email & Integrations" },
  { key: "reports", label: "Reports & Notifications" },
  { key: "security", label: "Compliance & Security", roles: ["admin", "supervisor"] },
  { key: "danger", label: "Danger Zone", roles: ["admin"] },
];
const Group = ({ label, desc, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">{label}</div>
    {desc && <p className="text-xs text-[#1C2530]/50 mb-2">{desc}</p>}
    {children}
  </div>
);

const BRAND_FIELDS = [
  ["primary", "Primary"], ["secondary", "Secondary"], ["background", "Background"], ["surface", "Surface"],
  ["text_primary", "Text Primary"], ["text_secondary", "Text Secondary"], ["border", "Border"],
  ["success", "Success"], ["warning", "Warning"], ["error", "Error"],
];
const _lum = (hex) => {
  const h = hex.replace("#", ""); const v = [0, 2, 4].map((i) => {
    let c = parseInt(h.substr(i, 2), 16) / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }); return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
};
const _ratio = (a, b) => { try { const l1 = _lum(a), l2 = _lum(b); return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05); } catch { return 0; } };
const WCAG_CHECKS = [
  ["text_primary", "background", 4.5, "Body text / background"],
  ["text_secondary", "background", 4.5, "Secondary text / background"],
  ["text_primary", "surface", 4.5, "Text / cards"],
  ["primary", "surface", 3.0, "Primary accent / surface"],
];

function ProposalConfig() {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/proposals/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/proposals/config", {
        max_discount_pct: Number(cfg.max_discount_pct), currency: cfg.currency,
        proposal_mode: cfg.proposal_mode || "combined", discount_ladder: cfg.discount_ladder || [],
        owner_id: cfg.owner_id || "",
        ...(cfg.min_margin_pct === "" || cfg.min_margin_pct == null ? { clear_min_margin: true } : { min_margin_pct: Number(cfg.min_margin_pct) }),
        min_margin_approver_role: cfg.min_margin_approver_role || "supervisor",
        ...(cfg.loss_alert_pct === "" || cfg.loss_alert_pct == null ? { clear_loss_alert: true } : { loss_alert_pct: Number(cfg.loss_alert_pct) }),
      });
      setCfg(r.data); toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const suggest = async () => {
    try { const r = await api.get("/proposals/ladder/suggested"); setCfg((c) => ({ ...c, discount_ladder: r.data.ladder })); toast.success(`Suggested a ${r.data.ladder.length}-tier ladder for ${r.data.seat_limit} seats`); }
    catch { toast.error("Could not suggest a ladder"); }
  };
  if (!cfg) return <Loading />;
  const ownerPool = showAll ? users : users.filter((u) => u.role === "admin" || u.role === "supervisor");
  const roleKeys = Array.from(new Set([...(users.map((u) => u.role)), "admin", "supervisor"])).filter((k) => k && k !== "rep");
  const ladder = cfg.discount_ladder || [];
  const setTier = (i, f, v) => setCfg({ ...cfg, discount_ladder: ladder.map((t, j) => j === i ? { ...t, [f]: f === "max_pct" ? Number(v) : v } : t) });
  const inp = "border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]";
  return (
    <div className="border border-[#DFDAD0] bg-white p-5 space-y-5" data-testid="proposal-config">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Proposal Settings owner</label>
          <select value={cfg.owner_id || ""} onChange={(e) => setCfg({ ...cfg, owner_id: e.target.value })} data-testid="proposal-owner-select" className={`block mt-1 ${inp} w-64`}>
            <option value="">— Not assigned —</option>
            {ownerPool.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABEL[u.role] || u.role}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-[#1C2530]/60 pb-2 cursor-pointer" data-testid="owner-showall-label">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="owner-showall-toggle" /> Show all users (incl. reps)
        </label>
      </div>

      <div className="flex flex-wrap items-end gap-4 pt-4 border-t border-[#DFDAD0]">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Max discount %</label>
          <input type="number" value={cfg.max_discount_pct} onChange={(e) => setCfg({ ...cfg, max_discount_pct: e.target.value })} data-testid="proposal-max-discount" className={`block mt-1 ${inp} w-28`} />
        </div>
        <div>
          <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Currency</label>
          <input value={cfg.currency} onChange={(e) => setCfg({ ...cfg, currency: e.target.value })} data-testid="proposal-currency" className={`block mt-1 ${inp} w-24`} />
        </div>
        <div data-testid="margin-floor-block">
          <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Min gross margin % (blank = off)</label>
          <div className="flex items-center gap-2 mt-1">
            <input type="number" min="0" max="100" value={cfg.min_margin_pct ?? ""} onChange={(e) => setCfg({ ...cfg, min_margin_pct: e.target.value })} data-testid="proposal-min-margin" placeholder="e.g. 35" className={`${inp} w-24`} />
            <span className="text-xs text-[#1C2530]/60">below this needs</span>
            <select value={cfg.min_margin_approver_role || "supervisor"} onChange={(e) => setCfg({ ...cfg, min_margin_approver_role: e.target.value })} data-testid="proposal-min-margin-role" className={inp}>
              <option value="supervisor">Sales Head</option><option value="admin">Admin</option>
            </select>
          </div>
          <div className="text-[11px] text-[#1C2530]/50 mt-1">Margin = (list − discount) − partner costs. Whichever is stricter — discount ladder or margin floor — decides the approver.</div>
        </div>
        <div data-testid="loss-alert-block">
          <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Loss-pattern alert % (blank = off)</label>
          <div className="flex items-center gap-2 mt-1">
            <input type="number" min="1" max="100" value={cfg.loss_alert_pct ?? ""} onChange={(e) => setCfg({ ...cfg, loss_alert_pct: e.target.value })} data-testid="proposal-loss-alert" placeholder="e.g. 40" className={`${inp} w-24`} />
            <span className="text-xs text-[#1C2530]/60">of a month's losses sharing one reason → notify Sales Heads & Admins</span>
          </div>
          <div className="text-[11px] text-[#1C2530]/50 mt-1">Counts rejected proposals and lost leads (min. 3 losses). One alert per reason per month.</div>
        </div>
        <div>
          <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Proposal format</label>
          <select value={cfg.proposal_mode || "combined"} onChange={(e) => setCfg({ ...cfg, proposal_mode: e.target.value })} data-testid="proposal-mode-select" className={`block mt-1 ${inp} w-72`}>
            <option value="combined">Single combined techno-commercial proposal</option>
            <option value="split">Separate technical &amp; commercial proposals</option>
          </select>
        </div>
      </div>

      <div className="pt-4 border-t border-[#DFDAD0]">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Discount approval ladder</div>
            <div className="text-xs text-[#1C2530]/50 max-w-lg">A discount up to each threshold routes to that role for sign-off; a sender at or above that role can send directly. Until configured, all discounts default to Admin — deals are never blocked.</div>
          </div>
          <button onClick={suggest} data-testid="ladder-suggest-btn" className="font-mono text-[10px] uppercase border border-[#DFDAD0] px-2 py-1 hover:bg-[#EFECE5] shrink-0">Suggest from team size</button>
        </div>
        <div className="space-y-2" data-testid="ladder-editor">
          {ladder.length === 0 && <div className="font-mono text-[10px] text-[#E2711D]">Not configured — defaults to Admin for all discounts.</div>}
          {ladder.map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`ladder-tier-${i}`}>
              <span className="font-mono text-[10px] text-[#1C2530]/50">up to</span>
              <input type="number" value={t.max_pct} onChange={(e) => setTier(i, "max_pct", e.target.value)} data-testid={`ladder-max-${i}`} className={`${inp} w-20`} />
              <span className="font-mono text-[10px] text-[#1C2530]/50">% → approver</span>
              <select value={t.approver_role} onChange={(e) => setTier(i, "approver_role", e.target.value)} data-testid={`ladder-role-${i}`} className={`${inp} w-40`}>
                {roleKeys.map((k) => <option key={k} value={k}>{ROLE_LABEL[k] || k}</option>)}
              </select>
              <button onClick={() => setCfg({ ...cfg, discount_ladder: ladder.filter((_, j) => j !== i) })} data-testid={`ladder-rm-${i}`} className="text-[#B23A2E] hover:bg-[#B23A2E]/10 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={() => setCfg({ ...cfg, discount_ladder: [...ladder, { max_pct: 100, approver_role: "admin" }] })} data-testid="ladder-add-btn" className="flex items-center gap-1 font-mono text-[10px] uppercase border border-[#DFDAD0] px-2 py-1 hover:bg-[#EFECE5]"><Plus className="w-3 h-3" /> Add tier</button>
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="proposal-config-save" className="bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-xs font-mono uppercase hover:bg-[#2a3644] disabled:opacity-40">Save proposal settings</button>
    </div>
  );
}

function Branding() {
  const [b, setB] = useState(null);
  const [logo, setLogo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/tenant/branding").then((r) => { const { logo: lg, ...rest } = r.data; setB(rest); setLogo(lg || ""); }).catch(() => {}); }, []);
  if (!b) return <Loading />;
  const set = (k, v) => setB((s) => ({ ...s, [k]: v }));
  const fails = WCAG_CHECKS.map(([fg, bg, mn, label]) => ({ label, mn, r: _ratio(b[fg], b[bg]) })).filter((x) => x.r < x.mn);
  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 250000) { toast.error("Logo too large (max 250KB)"); return; }
    const rd = new FileReader(); rd.onload = () => setLogo(rd.result); rd.readAsDataURL(f);
  };
  const save = async () => {
    if (fails.length) { toast.error("Fix contrast warnings before saving"); return; }
    setBusy(true);
    try { await api.put("/tenant/branding", { ...b, logo }); toast.success("Branding saved — reload to apply everywhere"); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : (d?.message || "Save failed")); }
    setBusy(false);
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="branding-editor">
      <div className="border border-[#DFDAD0] bg-white p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {BRAND_FIELDS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-xs text-[#1C2530]">
              <input type="color" value={b[k]} onChange={(e) => set(k, e.target.value)}
                data-testid={`brand-${k}`} className="w-7 h-7 border border-[#DFDAD0] p-0" />
              <span className="flex-1">{label}</span>
              <span className="font-mono text-[9px] text-[#1C2530]/50">{b[k]}</span>
            </label>
          ))}
        </div>
        <div className="space-y-1">
          <div className="font-mono text-[9px] uppercase text-[#1C2530]/50">Login headline</div>
          <input value={b.login_headline || ""} onChange={(e) => set("login_headline", e.target.value)}
            data-testid="brand-login-headline" className="w-full border border-[#DFDAD0] px-2 py-1.5 text-sm" />
          <input value={b.login_subtext || ""} onChange={(e) => set("login_subtext", e.target.value)}
            data-testid="brand-login-subtext" placeholder="Login subtext" className="w-full border border-[#DFDAD0] px-2 py-1.5 text-sm" />
        </div>
        <div className="space-y-1">
          <div className="font-mono text-[9px] uppercase text-[#1C2530]/50">Logo (PNG/SVG, ≤250KB)</div>
          <input type="file" accept="image/*" onChange={onLogo} data-testid="brand-logo-upload" className="text-xs" />
          {logo && <button onClick={() => setLogo("")} className="ml-2 font-mono text-[9px] text-[#B23A2E]">clear</button>}
        </div>
        {fails.length > 0 ? (
          <div className="border border-[#B23A2E]/40 bg-[#B23A2E]/10 p-2 space-y-0.5" data-testid="wcag-warnings">
            {fails.map((f) => (
              <div key={f.label} className="text-[11px] text-[#7d271d]">✗ {f.label}: {f.r.toFixed(2)}:1 (needs {f.mn}:1)</div>
            ))}
          </div>
        ) : (
          <div className="text-[11px] text-[#2F7A3E]" data-testid="wcag-pass">✓ All contrast checks pass (WCAG AA)</div>
        )}
        <button onClick={save} disabled={busy || fails.length > 0} data-testid="brand-save"
          className="w-full bg-[#1C2530] text-white py-2 text-sm disabled:opacity-40">{busy ? "Saving…" : "Save branding"}</button>
      </div>
      <div className="border border-[#DFDAD0] p-4" style={{ background: b.background }} data-testid="brand-preview">
        <div className="flex items-center gap-2 mb-4 p-2 rounded" style={{ background: b.secondary }}>
          <div className="w-7 h-7 flex items-center justify-center overflow-hidden" style={{ background: b.primary }}>
            {logo ? <img src={logo} alt="" className="w-full h-full object-contain" /> : <span className="text-white text-[10px] font-bold">A</span>}
          </div>
          <span className="text-sm font-medium text-white">{b.login_headline || "Workspace"}</span>
        </div>
        <div className="rounded p-4" style={{ background: b.surface, border: `1px solid ${b.border}` }}>
          <div className="text-lg font-semibold" style={{ color: b.text_primary }}>{b.login_headline || "AI Sales Team"}</div>
          <div className="text-sm mb-3" style={{ color: b.text_secondary }}>{b.login_subtext || "Sign in to your workspace"}</div>
          <button className="w-full py-2 text-sm text-white rounded" style={{ background: b.primary }}>Sign in</button>
          <div className="flex gap-2 mt-3">
            <span className="text-[10px] px-2 py-0.5 rounded text-white" style={{ background: b.success }}>Success</span>
            <span className="text-[10px] px-2 py-0.5 rounded text-white" style={{ background: b.warning }}>Warning</span>
            <span className="text-[10px] px-2 py-0.5 rounded text-white" style={{ background: b.error }}>Error</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SystemSender() {
  const [info, setInfo] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/mailbox/system-sender").then((r) => { setInfo(r.data); setVal(r.data.system_sender_email || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/mailbox/system-sender", { system_sender_email: val }); toast.success(val ? `System emails will send as ${val}` : "Cleared — sends as the connected mailbox"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  if (!info) return null;
  const effective = info.forced_managed ? "platform sender" : (info.system_sender_email || info.connected_email || "managed sender (no mailbox connected)");
  return (
    <div className="p-5" data-testid="system-sender">
      <div className="text-sm font-medium text-[#1C2530]">Send system emails as</div>
      <div className="text-xs text-[#1C2530]/50 mt-0.5">Digests, approvals, nudges, guide packs and password emails. Leave blank to send from the connected mailbox itself. Currently: <span className="text-[#1C2530]" data-testid="system-sender-effective">{effective}</span></div>
      <div className="mt-3 flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="crm@yourcompany.com" data-testid="system-sender-input"
          className="flex-1 border border-[#DFDAD0] px-3 py-2 text-sm bg-white text-[#1C2530]" />
        <button onClick={save} disabled={busy} data-testid="system-sender-save" className="border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5] disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
      </div>
      {info.system_sender_email && info.provider === "outlook" && info.shared_send_scope === false && (
        <div className="mt-2 font-mono text-[10px] text-[#E2711D]" data-testid="system-sender-rescope">
          Outlook needs the "Send as shared mailbox" permission: disconnect and reconnect Outlook once, and make sure {info.connected_email} has Send As rights on {info.system_sender_email} in Microsoft 365. Until then mail sends as {info.connected_email}.
        </div>
      )}
      {info.system_sender_email && info.provider === "gmail" && (
        <div className="mt-2 font-mono text-[10px] text-[#1C2530]/60">Gmail: {info.system_sender_email} must be a verified "Send mail as" alias of {info.connected_email}.</div>
      )}
    </div>
  );
}


function TenantMailbox() {
  const [mb, setMb] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/mailbox/status").then((r) => setMb(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("mailbox") === "connected") toast.success(`${p.get("provider") === "outlook" ? "Outlook" : "Gmail"} mailbox connected`);
    if (p.get("mailbox_error")) toast.error("Mailbox connection failed — please try again");
    if (p.get("mailbox") || p.get("mailbox_error")) window.history.replaceState({}, "", "/settings");
  }, []);
  const connect = async (provider) => {
    setBusy(provider);
    try { const { data } = await api.get(`/mailbox/${provider}/connect`); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot connect"); setBusy(""); }
  };
  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect the ${provider === "outlook" ? "Outlook" : "Gmail"} mailbox for this workspace?`)) return;
    try { await api.delete(`/mailbox/${provider}`); toast.success("Mailbox disconnected"); load(); }
    catch (e) { toast.error("Failed to disconnect"); }
  };
  if (!mb) return <Loading />;
  const Row = ({ provider, label }) => {
    const s = mb[provider] || {};
    return (
      <div className="p-5 flex items-center justify-between" data-testid={`tenant-mailbox-${provider}`}>
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-[#1C2530]/60" />
          <div>
            <div className="text-sm font-medium text-[#1C2530]">{label}</div>
            {s.connected
              ? <div className="text-xs text-[#2F7A3E] mt-0.5" data-testid={`mailbox-status-${provider}`}>Connected · {s.email}{s.connected_by ? ` · by ${s.connected_by}` : ""}</div>
              : s.status === "reauthorization_required"
                ? <div className="text-xs text-[#B23A2E] mt-0.5" data-testid={`mailbox-status-${provider}`}>Reconnection required</div>
                : <div className="text-xs text-[#1C2530]/50 mt-0.5" data-testid={`mailbox-status-${provider}`}>Not connected</div>}
          </div>
        </div>
        {s.connected ? (
          <button data-testid={`mailbox-disconnect-${provider}`} onClick={() => disconnect(provider)}
            className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E] hover:underline">Disconnect</button>
        ) : (
          <button data-testid={`mailbox-connect-${provider}`} onClick={() => connect(provider)}
            disabled={!s.provider_configured || busy === provider}
            className="flex items-center gap-2 border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5] disabled:opacity-40">
            {busy === provider ? "Redirecting…" : `Connect ${label}`}
          </button>
        )}
      </div>
    );
  };
  const anyConfigured = mb.gmail?.provider_configured || mb.outlook?.provider_configured;
  return (
    <div className="border border-[#DFDAD0] bg-white divide-y divide-[#DFDAD0]" data-testid="tenant-mailbox">
      <Row provider="gmail" label="Gmail" />
      <Row provider="outlook" label="Outlook" />
      <SystemSender />
      {!anyConfigured && (
        <div className="p-4 font-mono text-[10px] text-[#E2711D]" data-testid="mailbox-no-config">
          Platform OAuth apps not configured yet — add Gmail/Outlook client credentials in "Email Sending OAuth" below.
        </div>
      )}
    </div>
  );
}

function ConnectedInbox() {
  const [mb, setMb] = useState(null);
  const load = () => api.get("/me/mailbox").then((r) => setMb(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("inbox") === "connected") toast.success("Inbox connected");
    if (p.get("inbox_error")) toast.error("Inbox connection failed — check admin OAuth config");
    if (p.get("inbox") || p.get("inbox_error")) window.history.replaceState({}, "", "/settings");
  }, []);
  const connect = async (provider) => {
    try { const { data } = await api.get(`/oauth/${provider}/connect`); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot connect"); }
  };
  const disconnect = async () => {
    try { await api.delete("/me/mailbox"); toast.success("Inbox disconnected"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  if (!mb) return <Loading />;
  const conn = mb.connection, cfg = mb.providers_configured || {};
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="connected-inbox">
      {conn ? (
        <div className="flex items-center justify-between">
          <div className="text-sm text-[#1C2530]">Connected: <span className="font-medium capitalize">{conn.provider}</span> · {conn.email}
            {conn.provider === "outlook" && (conn.calendar_scope
              ? <span className="ml-2 font-mono text-[10px] uppercase text-[#3E8F99]" data-testid="calendar-scope-on">Calendar connected · bookings create real Teams events (one-way)</span>
              : <span className="ml-2 font-mono text-[10px] uppercase text-[#E2711D]" data-testid="calendar-scope-off">Calendar not granted — Disconnect, then Connect Outlook again to enable real Teams bookings</span>)}
          </div>
          <button data-testid="inbox-disconnect-btn" onClick={disconnect} className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E] hover:underline">Disconnect</button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <button data-testid="connect-gmail-btn" onClick={() => connect("gmail")} disabled={!cfg.gmail}
            className="flex items-center gap-2 border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5] disabled:opacity-40">
            <Mail className="w-4 h-4" /> Connect Gmail
          </button>
          <button data-testid="connect-outlook-btn" onClick={() => connect("outlook")} disabled={!cfg.outlook}
            className="flex items-center gap-2 border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5] disabled:opacity-40">
            <Mail className="w-4 h-4" /> Connect Outlook
          </button>
          {!cfg.gmail && !cfg.outlook && <span className="font-mono text-[10px] text-[#E2711D]">Admin must add OAuth keys first</span>}
        </div>
      )}
      {conn && <MailboxTest />}
      {conn && <MailboxDiscovery />}
    </div>
  );
}

function MailboxTest() {
  const { user } = useAuth();
  const [res, setRes] = useState(null);
  const [all, setAll] = useState(null);
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { const { data } = await api.post("/mail/test"); setRes(data); data.ok ? toast.success(`Inbox reachable · ${data.inbox_sample} messages · ${data.ms} ms`) : toast.error(data.error); } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } finally { setBusy(false); } };
  const runAll = async () => { setBusy(true); try { const { data } = await api.post("/mail/test-all"); setAll(data); toast.success(`${data.ok}/${data.tested} mailboxes OK`); } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } finally { setBusy(false); } };
  return (
    <div className="mt-4 pt-4 border-t border-[#DFDAD0]" data-testid="mailbox-test">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={run} disabled={busy} data-testid="mailbox-test-btn" className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#EFECE5] disabled:opacity-40">Test connection</button>
        {user.role === "admin" && <button onClick={runAll} disabled={busy} data-testid="mailbox-test-all-btn" className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#EFECE5] disabled:opacity-40">Test all connected mailboxes</button>}
        <button onClick={async () => { try { const { data } = await api.get("/mail/weekly-digest/preview"); setDigest(data); } catch { toast.error("Preview failed"); } }} data-testid="weekly-digest-preview-btn" className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#EFECE5]">Preview my weekly digest</button>
        {(user.role === "admin" || user.role === "supervisor") && (
          <button onClick={async () => { setBusy(true); try { const { data } = await api.post("/digest/send-now"); data.suppressed ? toast.info(`Digest queued for ${data.to} — emails are suppressed in preview. In production it arrives from ${data.expected_from}.`) : toast.success(`Digest sent to ${data.to} — check that it arrived from ${data.expected_from}`); } catch (e) { toast.error(e.response?.data?.detail || "Could not send digest"); } finally { setBusy(false); } }}
            disabled={busy} data-testid="digest-send-now-btn" className="border border-[#1C2530] bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">Send my digest now</button>
        )}
        {res && <span data-testid="mailbox-test-result" className={`text-xs ${res.ok ? "text-[#2F7A3E]" : "text-[#B23A2E]"}`}>{res.ok ? `OK · ${res.provider} · ${res.inbox_sample} inbox messages read · ${res.ms} ms` : `Failed · ${res.error}`}</span>}
      </div>
      {digest && (
        <div className="mt-3 border border-[#DFDAD0] bg-white p-3" data-testid="weekly-digest-preview">
          <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Weekly digest · sent Mondays 08:00 · what you would receive now</div>
          {digest.empty ? <div className="text-xs text-[#1C2530]/50 italic">Nothing to report yet — send some emails from Mail first.</div> : <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: digest.html }} />}
        </div>
      )}
      {all && (
        <table className="w-full text-xs mt-3" data-testid="mailbox-test-all-table"><tbody>
          {all.results.length === 0 && <tr><td className="py-1 text-[#1C2530]/50 italic">No connected mailboxes in this workspace.</td></tr>}
          {all.results.map((r) => <tr key={r.user_email} className="border-t border-[#DFDAD0]"><td className="py-1.5 pr-3">{r.user}</td><td className="py-1.5 pr-3 text-[#1C2530]/60">{r.email} · {r.provider}</td>
            <td className={`py-1.5 ${r.ok ? "text-[#2F7A3E]" : "text-[#B23A2E]"}`}>{r.ok ? `OK · ${r.inbox_sample} msgs · ${r.ms} ms` : r.error}</td></tr>)}
        </tbody></table>
      )}
    </div>
  );
}

function MailboxDiscovery() {
  const [summary, setSummary] = useState(null);
  const load = () => api.get("/me/mailbox/scan/summary").then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (enabled) => {
    try {
      await api.post("/me/mailbox/scan/optin", { enabled });
      setSummary((s) => ({ ...s, opt_in: enabled }));
      toast.success(enabled ? "Daily inbox auto-scan turned ON" : "Daily inbox auto-scan turned OFF");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  if (!summary) return null;
  const st = summary.state;
  return (
    <div className="mt-5 border-t border-[#DFDAD0] pt-4" data-testid="mailbox-discovery">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-[#1C2530]">Contact Discovery · daily auto-scan</div>
          <p className="text-xs text-[#1C2530]/50 mt-1">When ON, we scan your last 180 days of sent mail (up to 2,000 messages) each night and add new people you've emailed to your pipeline as Suspect leads. OFF by default — your inbox is never scanned until you opt in here. Connecting your inbox above does not enable this.</p>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer" data-testid="scan-optin-label">
          <input type="checkbox" data-testid="scan-optin-toggle" checked={!!summary.opt_in} onChange={(e) => toggle(e.target.checked)} />
          <span className="text-xs font-mono uppercase tracking-wider text-[#1C2530]/70">{summary.opt_in ? "On" : "Off"}</span>
        </label>
      </div>
      {summary.opt_in && (st && st.last_run_at ? (
        <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50" data-testid="mailbox-scan-summary">
          {st.status === "error"
            ? <span className="text-[#B23A2E]">Last scan failed — reconnect your inbox to grant read access</span>
            : <>Last scan {new Date(st.last_run_at).toLocaleString()} · {st.added_leads || 0} added · {st.skipped_existing || 0} already in CRM</>}
        </div>
      ) : (
        <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/40" data-testid="mailbox-scan-summary">Opted in — your first nightly scan will populate the pipeline.</div>
      ))}
    </div>
  );
}

function EmailModelPicker() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/email/model-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const save = async (field, val) => {
    try { await api.put("/email/model-config", { [field]: val }); setCfg((c) => ({ ...c, [field]: val })); toast.success("Email model updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!cfg) return <Loading />;
  const Sel = ({ field, label, opts }) => (
    <div className="flex-1">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50 mb-1">{label}</div>
      <select data-testid={`${field.replace("_", "-")}-select`} value={cfg[field]} onChange={(e) => save(field, e.target.value)}
        className="w-full border border-[#DFDAD0] p-2 text-sm bg-white">
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  return (
    <div className="border border-[#DFDAD0] bg-white p-4 flex gap-6" data-testid="email-model-picker">
      <Sel field="groq_model" label="Groq · primary" opts={cfg.groq_options} />
      <Sel field="gemini_model" label="Gemini · fallback" opts={cfg.gemini_options} />
    </div>
  );
}

function EmailOAuthConfig() {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ gmail: { client_id: "", client_secret: "" }, outlook: { client_id: "", client_secret: "", tenant: "common" } });
  const load = () => api.get("/email/oauth-config").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);
  const save = async (provider) => {
    const v = f[provider];
    if (!v.client_id.trim() || !v.client_secret.trim()) { toast.error("Client ID and Secret required"); return; }
    try {
      await api.put(`/email/oauth-config/${provider}`, { client_id: v.client_id, client_secret: v.client_secret, tenant: v.tenant });
      toast.success(`${provider} OAuth saved`); load();
      setF((s) => ({ ...s, [provider]: { ...s[provider], client_secret: "" } }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!cfg) return <Loading />;
  const Row = ({ provider, showTenant }) => (
    <div className="p-4 space-y-2" data-testid={`oauth-cfg-${provider}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-[#1C2530] capitalize">{provider}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider">{cfg[provider]?.configured ? <span className="text-[#2F7A3E]">configured · {cfg[provider].client_id_masked}</span> : <span className="text-[#E2711D]">not set</span>}</div>
      </div>
      <input data-testid={`oauth-${provider}-clientid`} placeholder="Client ID" value={f[provider].client_id}
        onChange={(e) => setF((s) => ({ ...s, [provider]: { ...s[provider], client_id: e.target.value } }))}
        className="w-full border border-[#DFDAD0] p-2 text-sm" />
      <input data-testid={`oauth-${provider}-secret`} type="password" placeholder="Client Secret" value={f[provider].client_secret}
        onChange={(e) => setF((s) => ({ ...s, [provider]: { ...s[provider], client_secret: e.target.value } }))}
        className="w-full border border-[#DFDAD0] p-2 text-sm" />
      {showTenant && <input data-testid="oauth-outlook-tenant" placeholder="Tenant (or 'common')" value={f[provider].tenant}
        onChange={(e) => setF((s) => ({ ...s, [provider]: { ...s[provider], tenant: e.target.value } }))}
        className="w-full border border-[#DFDAD0] p-2 text-sm" />}
      <button data-testid={`oauth-${provider}-save`} onClick={() => save(provider)}
        className="bg-[#1C2530] text-[#F5F3EE] px-4 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]">Save</button>
    </div>
  );
  return (
    <div className="border border-[#DFDAD0] bg-white divide-y divide-[#DFDAD0]" data-testid="email-oauth-config">
      <Row provider="gmail" showTenant={false} />
      <Row provider="outlook" showTenant={true} />
    </div>
  );
}

function TenantProfile() {
  const [tenant, setTenant] = useState(null);
  useEffect(() => { api.get("/tenant").then((r) => setTenant(r.data)); }, []);
  const save = async () => {
    try { await api.patch("/tenant/profile", { display_name: tenant.display_name, logo: tenant.logo || "" }); toast.success("Tenant profile saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!tenant) return <Loading />;
  return (
    <div className="border border-[#DFDAD0] bg-white flex flex-wrap items-center gap-2 p-4" data-testid="tenant-profile-row">
      <span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Tenant profile</span>
      <input value={tenant.display_name || ""} onChange={(e) => setTenant((t) => ({ ...t, display_name: e.target.value }))} placeholder="Display name" data-testid="tenant-display-name"
        className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530] w-52" />
      <input value={tenant.logo || ""} onChange={(e) => setTenant((t) => ({ ...t, logo: e.target.value }))} placeholder="https://…/logo.png" data-testid="tenant-logo"
        className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530] flex-1 min-w-[200px]" />
      <button onClick={save} data-testid="tenant-profile-save" className="bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs hover:bg-[#2a3644]">Save</button>
      <span className="text-xs text-[#1C2530]/40 w-full">Used on the login screen & report emails — foundation for white-labeling.</span>
    </div>
  );
}

function SendingDomain() {
  const [domainEdit, setDomainEdit] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/tenant").then((r) => { setDomainEdit(r.data.sending_domain || ""); setLoaded(true); }); }, []);
  const save = async () => {
    try { const { data } = await api.patch("/tenant/sending-domain", { sending_domain: domainEdit }); setDomainEdit(data.sending_domain); toast.success(`Sending domain set to @${data.sending_domain}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!loaded) return <Loading />;
  return (
    <div className="border border-[#DFDAD0] bg-white flex items-center gap-2 p-4" data-testid="sending-domain-row">
      <span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Sending domain</span>
      <span className="text-[#1C2530]/40">@</span>
      <input value={domainEdit} onChange={(e) => setDomainEdit(e.target.value)} data-testid="sending-domain-input"
        className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530] w-56" />
      <button onClick={save} data-testid="sending-domain-save" className="bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs hover:bg-[#2a3644]">Save</button>
      <span className="text-xs text-[#1C2530]/40 ml-auto">Per-tenant · recipients must use this domain</span>
    </div>
  );
}

function ReportRecipients() {
  const [rows, setRows] = useState(null);
  const [dom, setDom] = useState("visolutionz.com");
  const [f, setF] = useState({ email: "", cadence: "weekly", detail_level: "summary" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/report-recipients").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/tenant").then((r) => setDom(r.data.sending_domain || "visolutionz.com")); }, []);
  const add = async () => {
    if (!f.email.trim()) { toast.error("Recipient email required"); return; }
    setBusy(true);
    try { await api.post("/report-recipients", f); setF({ email: "", cadence: "weekly", detail_level: "summary" }); load(); toast.success("Recipient added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const patch = async (r, upd) => {
    try { await api.patch(`/report-recipients/${r.id}`, upd); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.email} from report distribution?`)) return;
    setRows((rs) => (rs || []).filter((x) => x.id !== r.id));
    try { await api.delete(`/report-recipients/${r.id}`); toast.success("Removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); load(); }
  };
  const sel = "border border-[#DFDAD0] px-2 py-1.5 text-xs bg-white outline-none focus:border-[#1C2530]";
  return (
    <div className="border border-[#DFDAD0] bg-white" data-testid="report-distribution">
      <div className="p-4 border-b border-[#DFDAD0] bg-[#EFECE5]">
        <div className="flex gap-2 items-center">
          <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder={`recipient@${dom}`} data-testid="report-recipient-email"
            className="flex-1 border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
          <select value={f.cadence} onChange={(e) => setF({ ...f, cadence: e.target.value })} data-testid="report-recipient-cadence" className={sel}>
            {REPORT_CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f.detail_level} onChange={(e) => setF({ ...f, detail_level: e.target.value })} data-testid="report-recipient-level" className={sel}>
            {REPORT_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <button onClick={add} disabled={busy} data-testid="report-recipient-add" className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-3 py-2 text-xs font-medium hover:bg-[#2a3644] disabled:opacity-50"><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
        <div className="text-xs text-[#1C2530]/50 mt-2">Recipients get pipeline change reports by email — no CRM login needed. @{dom} only.</div>
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-[#1C2530]/50" data-testid="report-empty">No recipients yet.</div>
      ) : (
        <div className="divide-y divide-[#DFDAD0]">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-4" data-testid={`report-row-${r.email}`}>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#3E8F99]" /><span className="text-sm text-[#1C2530]">{r.email}</span></div>
              <div className="flex items-center gap-2">
                <select value={r.cadence} onChange={(e) => patch(r, { cadence: e.target.value })} data-testid={`report-cadence-${r.email}`} className={sel}>
                  {REPORT_CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={r.detail_level} onChange={(e) => patch(r, { detail_level: e.target.value })} data-testid={`report-level-${r.email}`} className={sel}>
                  {REPORT_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <button onClick={() => del(r)} data-testid={`report-del-${r.email}`} className="text-[#B23A2E] p-1.5 hover:bg-[#B23A2E]/5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const APPROVAL_ROWS = [
  { key: "sequence", label: "Routine Sequence Emails", hint: "Safe to Auto once drafts are trusted." },
  { key: "objection", label: "Objection Handling", hint: "Highest cost if tone is wrong — recommended Draft." },
  { key: "post_reply", label: "Post-Reply Messages", hint: "Any message after a prospect reply — recommended Draft." },
];
const NOTIF_ROWS = [
  { key: "hot_lead", label: "Hot / Qualified lead alerts" },
  { key: "priority", label: "Priority lead alerts" },
  { key: "assignment", label: "New lead assigned to me" },
  { key: "campaign_promoted", label: "Queued LinkedIn campaign activated" },
];
const DIGEST_OPTIONS = [
  { v: "daily", label: "Daily" },
  { v: "weekly", label: "Weekly" },
  { v: "off", label: "Off" },
];

function DigestChoice({ value, onChange, testid }) {
  return (
    <div className="flex border border-[#DFDAD0]" data-testid={testid}>
      {DIGEST_OPTIONS.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} data-testid={`digest-opt-${o.v}`}
          className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${value === o.v ? "bg-[#1C2530] text-[#F5F3EE]" : "text-[#1C2530]/50 hover:bg-[#EFECE5]"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ value, on = "auto", off = "draft", labels = ["Auto-send", "Draft"], onChange, testid }) {
  return (
    <div className="flex border border-[#DFDAD0]" data-testid={testid}>
      {[off, on].map((v, i) => (
        <button key={v} onClick={() => onChange(v)}
          className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${value === v ? (v === on ? "bg-[#E2711D] text-white" : "bg-[#1C2530] text-[#F5F3EE]") : "text-[#1C2530]/50 hover:bg-[#EFECE5]"}`}>
          {i === 0 ? labels[1] : labels[0]}
        </button>
      ))}
    </div>
  );
}

function OnOff({ value, onChange, testid }) {
  return (
    <div className="flex border border-[#DFDAD0]" data-testid={testid}>
      {[false, true].map((v) => (
        <button key={String(v)} onClick={() => onChange(v)}
          className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-wider ${value === v ? (v ? "bg-[#2F7A3E] text-white" : "bg-[#94A3AD] text-white") : "text-[#1C2530]/50 hover:bg-[#EFECE5]"}`}>
          {v ? "On" : "Muted"}
        </button>
      ))}
    </div>
  );
}

function MfaSection({ enabled }) {
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [done, setDone] = useState(enabled);
  const start = async () => { const { data } = await api.post("/auth/mfa/enroll"); setEnroll(data); };
  const verify = async () => {
    try { await api.post("/auth/mfa/verify", { code }); setDone(true); setEnroll(null); toast.success("MFA enabled"); }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="mfa-settings">
      <div className="flex items-start gap-3">
        {done ? <ShieldCheck className="w-5 h-5 text-[#2F7A3E] mt-0.5" /> : <KeyRound className="w-5 h-5 text-[#1C2530] mt-0.5" />}
        <div className="flex-1">
          <div className="text-sm font-medium text-[#1C2530]">Authenticator app (MFA)</div>
          <div className="text-xs text-[#1C2530]/60 mt-0.5 max-w-md">Required to use the Admin self-service password reset. Enroll once with Google Authenticator or Authy.</div>
          {done && <div className="mt-2 font-mono text-[10px] uppercase text-[#2F7A3E]" data-testid="mfa-enabled-badge">MFA enabled</div>}
          {!done && !enroll && <button onClick={start} data-testid="mfa-enroll-btn" className="mt-3 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-sm hover:bg-[#2a3644]">Set up MFA</button>}
          {enroll && (
            <div className="mt-3 space-y-3">
              <img src={enroll.qr_png} alt="MFA QR" className="w-40 h-40 border border-[#DFDAD0]" data-testid="mfa-qr" />
              <div className="font-mono text-[10px] text-[#1C2530]/50 break-all">Secret: {enroll.secret}</div>
              <div className="flex items-center gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="mfa-verify-input"
                  className="w-32 border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
                <button onClick={verify} data-testid="mfa-verify-btn" className="bg-[#2F7A3E] text-white px-4 py-2 text-sm hover:bg-[#256030]">Verify & enable</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IntegrationsVault() {
  const [rows, setRows] = useState(null);
  const [keys, setKeys] = useState({});
  const [test, setTest] = useState({});
  const load = () => api.get("/integrations").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async (svc) => {
    if (!keys[svc] || keys[svc].length < 6) { toast.error("Enter a valid key"); return; }
    try { const { data } = await api.put(`/integrations/${svc}`, { api_key: keys[svc] });
      toast.success(`${svc} key ${data.action}`); setKeys((k) => ({ ...k, [svc]: "" })); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const runTest = async (svc) => {
    setTest((t) => ({ ...t, [svc]: { loading: true } }));
    try { const { data } = await api.post(`/integrations/${svc}/test`); setTest((t) => ({ ...t, [svc]: data })); }
    catch (e) { setTest((t) => ({ ...t, [svc]: { ok: false, detail: e.response?.data?.detail || "Error" } })); }
  };
  if (!rows) return null;
  return (
    <div className="border border-[#DFDAD0] bg-white divide-y divide-[#DFDAD0]" data-testid="integrations-vault">
      {rows.map((r) => (
        <div key={r.service} className="p-4 flex items-center gap-3" data-testid={`vault-row-${r.service}`}>
          <div className="w-28">
            <div className="text-sm font-medium text-[#1C2530] capitalize">{r.service === "gemini" ? "Gemini" : r.service}</div>
            {(r.service === "groq" || r.service === "gemini") && (
              <div className="font-mono text-[8px] uppercase tracking-wider text-[#3E8F99]">{r.service === "groq" ? "Email · primary" : "Email · fallback"}</div>
            )}
          </div>
          {r.configured
            ? <span className="font-mono text-[10px] text-[#2F7A3E]" data-testid={`vault-status-${r.service}`}>SAVED {r.mask}</span>
            : <span className="font-mono text-[10px] text-[#1C2530]/40">NOT SET</span>}
          <input type="password" value={keys[r.service] || ""} onChange={(e) => setKeys((k) => ({ ...k, [r.service]: e.target.value }))}
            placeholder={r.configured ? "Enter new key to rotate" : "Paste API key"} data-testid={`vault-input-${r.service}`}
            className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-xs outline-none focus:border-[#1C2530]" />
          <button onClick={() => save(r.service)} data-testid={`vault-save-${r.service}`} className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[9px] uppercase hover:bg-[#EFECE5]">{r.configured ? "Rotate" : "Save"}</button>
          <button onClick={() => runTest(r.service)} disabled={!r.configured} data-testid={`vault-test-${r.service}`} className="border border-[#3E8F99] text-[#3E8F99] px-3 py-1.5 font-mono text-[9px] uppercase hover:bg-[#3E8F99]/10 disabled:opacity-40">Test</button>
          {test[r.service] && (test[r.service].loading
            ? <span className="font-mono text-[9px] text-[#1C2530]/50">…</span>
            : <span className={`font-mono text-[9px] ${test[r.service].ok ? "text-[#2F7A3E]" : "text-[#E50914]"}`} data-testid={`vault-testresult-${r.service}`}>{test[r.service].ok ? "OK" : "FAIL"} · {test[r.service].detail}</span>)}
        </div>
      ))}
    </div>
  );
}

function SyntheticCleanup({ tenantId }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pruneDays, setPruneDays] = useState(30);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/cleanup/synthetic"); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };
  const run = async () => {
    if (!window.confirm(`Delete ${preview.approvals.count} synthetic approval rows and ${preview.notifications.count} notifications? Real users' records are never matched.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/cleanup/synthetic?confirm=${encodeURIComponent(tenantId)}`); toast.success(`Removed ${data.deleted.approvals} approvals, ${data.deleted.notifications} notifications`); setPreview(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); } finally { setBusy(false); }
  };
  const prune = async () => {
    if (!window.confirm(`Delete all READ notifications older than ${pruneDays} days for every user in this workspace?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/notifications/prune?days=${pruneDays}&confirm=${encodeURIComponent(tenantId)}`); toast.success(`Pruned ${data.deleted} read notifications`); }
    catch (e) { toast.error(e.response?.data?.detail || "Prune failed"); } finally { setBusy(false); }
  };
  const Sample = ({ rows, label }) => rows.length > 0 && (
    <div className="mt-2">
      <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">{label} sample</div>
      <ul className="text-xs text-[#1C2530]/70 space-y-0.5 max-h-32 overflow-y-auto">{rows.map((r) => <li key={r.id} className="truncate">· {r.title || r.body || r.id}{r.requested_by_name ? ` — ${r.requested_by_name}` : ""}</li>)}</ul>
    </div>
  );
  return (
    <div className="mt-6">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E]/80 mb-2">Pre-onboarding cleanup · synthetic residue</div>
      <div className="border border-[#DFDAD0] bg-white p-5 space-y-4" data-testid="synthetic-cleanup-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-[#1C2530]">Purge test approval history & notifications</div>
            <div className="text-xs text-[#1C2530]/60 mt-0.5 max-w-lg">Finds approval requests and notifications created by ZZ*/TESTONLY* test users or pointing at deleted test leads. Always preview first — real users' decision records are never matched.</div>
          </div>
          <button onClick={load} disabled={busy} data-testid="cleanup-preview-btn" className="shrink-0 border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5] disabled:opacity-40">{busy && !preview ? "Scanning…" : "Preview (dry run)"}</button>
        </div>
        {preview && (
          <div className="border-t border-[#DFDAD0] pt-3" data-testid="cleanup-preview">
            <div className="flex gap-6 text-sm">
              <span data-testid="cleanup-approvals-count"><span className="font-mono font-semibold">{preview.approvals.count}</span> approval rows</span>
              <span data-testid="cleanup-notifs-count"><span className="font-mono font-semibold">{preview.notifications.count}</span> notifications</span>
            </div>
            <Sample rows={preview.approvals.sample} label="Approvals" />
            <Sample rows={preview.notifications.sample} label="Notifications" />
            <button onClick={run} disabled={busy || (preview.approvals.count + preview.notifications.count === 0)} data-testid="cleanup-run-btn"
              className="mt-3 bg-[#B23A2E] text-white px-4 py-2 text-sm hover:bg-[#8f2d24] disabled:opacity-40">Delete matched rows</button>
          </div>
        )}
        <div className="border-t border-[#DFDAD0] pt-3 flex items-center gap-3 flex-wrap" data-testid="notif-prune-row">
          <div className="text-xs text-[#1C2530]/60 flex-1 min-w-[200px]">Optional: prune every user's <span className="font-medium">read</span> notifications older than</div>
          <input type="number" min="1" value={pruneDays} onChange={(e) => setPruneDays(Number(e.target.value) || 30)} data-testid="notif-prune-days" className="w-20 border border-[#DFDAD0] px-2 py-1.5 text-sm" />
          <span className="text-xs text-[#1C2530]/60">days</span>
          <button onClick={prune} disabled={busy} data-testid="notif-prune-btn" className="border border-[#DFDAD0] px-3 py-1.5 text-xs hover:bg-[#EFECE5] disabled:opacity-40">Prune</button>
        </div>
      </div>
    </div>
  );
}

function DangerZone({ tenantId }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reset-crm-data?confirm=${encodeURIComponent(tenantId)}`);
      const d = data.deleted || {};
      toast.success(`CRM data cleared — ${d.leads || 0} leads, ${d.contacts || 0} contacts, ${d.accounts || 0} accounts removed. Remaining: ${data.leads_remaining}.`);
      setOpen(false); setTyped("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E]/80 mb-2">Danger Zone · fresh start</div>
      <div className="border border-[#B23A2E]/50 bg-[#B23A2E]/5 p-5 flex items-center justify-between" data-testid="reset-crm-card">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#B23A2E] mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="text-sm font-medium text-[#1C2530]">Reset CRM data (start fresh)</div>
            <div className="text-xs text-[#1C2530]/60 mt-0.5 max-w-lg">Permanently deletes ALL leads, contacts, accounts and their activity / outreach / email / opportunity / import history for this workspace, so you can begin a clean production import. Users, roles, settings, branding and integrations are preserved. This cannot be undone.</div>
          </div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="reset-crm-open-btn"
          className="shrink-0 bg-[#B23A2E] text-white px-4 py-2 text-sm hover:bg-[#8f2d24]">Reset data…</button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reset-crm-dialog">
          <div className="bg-white border border-[#DFDAD0] max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-[#B23A2E]"><ShieldAlert className="w-5 h-5" /><span className="text-base font-semibold">Confirm CRM data reset</span></div>
            <p className="text-sm text-[#1C2530]/80">This will permanently delete every lead, contact and account (and their history) in this workspace. Configuration and user accounts are kept. <strong>This action cannot be undone.</strong></p>
            <p className="text-xs text-[#1C2530]/60">To confirm, type your workspace ID <span className="font-mono font-semibold text-[#1C2530]">{tenantId}</span> (or the word <span className="font-mono font-semibold text-[#1C2530]">RESET</span>) below:</p>
            <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="reset-crm-confirm-input"
              placeholder={tenantId} className="w-full border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#B23A2E]" />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => { setOpen(false); setTyped(""); }} data-testid="reset-crm-cancel-btn"
                className="border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5]">Cancel</button>
              <button onClick={reset} disabled={busy || !(typed.trim().toLowerCase() === String(tenantId || "").toLowerCase() || typed.trim().toUpperCase() === "RESET")} data-testid="reset-crm-confirm-btn"
                className="bg-[#B23A2E] text-white px-4 py-2 text-sm hover:bg-[#8f2d24] disabled:opacity-40">{busy ? "Clearing…" : "Delete all CRM data"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PipelineConfig() {
  const [cfg, setCfg] = useState(null);
  const [limits, setLimits] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/pipeline/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/settings").then((r) => setLimits(r.data.stage_limits || {})).catch(() => {});
  }, []);
  if (!cfg) return <Loading />;
  const setLabel = (s, v) => setCfg((c) => ({ ...c, labels: { ...c.labels, [s]: v } }));
  const setLimit = (s, v) => setLimits((l) => ({ ...l, [s]: v }));
  const toggleApproval = (s) =>
    setCfg((c) => ({ ...c, approval_stages: c.approval_stages.includes(s) ? c.approval_stages.filter((x) => x !== s) : [...c.approval_stages, s] }));
  const save = async () => {
    setBusy(true);
    try {
      const cleanLimits = {};
      Object.entries(limits).forEach(([k, v]) => { const n = parseInt(v, 10); if (n > 0) cleanLimits[k] = n; });
      const [{ data }] = await Promise.all([
        api.put("/pipeline/config", { labels: cfg.labels, approval_stages: cfg.approval_stages }),
        api.patch("/settings/stage-limits", { limits: cleanLimits }),
      ]);
      setCfg(data); toast.success("Pipeline configuration saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#DFDAD0] bg-white" data-testid="pipeline-config">
      <div className="grid grid-cols-[1.2fr_1.2fr_auto_auto] gap-2 px-4 py-2 bg-[#EFECE5] border-b border-[#DFDAD0] font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/60">
        <span>Internal stage (fixed)</span><span>Display label</span><span className="text-center">Time limit (days)</span><span className="text-center">Approval</span>
      </div>
      <div className="divide-y divide-[#DFDAD0]">
        {STAGES.map((s) => (
          <div key={s} className="grid grid-cols-[1.2fr_1.2fr_auto_auto] gap-2 items-center px-4 py-2.5" data-testid={`pipeline-cfg-row-${s}`}>
            <span className="font-mono text-[11px] text-[#1C2530]/55">{s}</span>
            <input value={cfg.labels[s] || ""} onChange={(e) => setLabel(s, e.target.value)} placeholder={s}
              data-testid={`pipeline-cfg-label-${s}`} className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
            <input type="number" min="0" value={limits[s] ?? ""} onChange={(e) => setLimit(s, e.target.value)} placeholder="—"
              data-testid={`pipeline-cfg-limit-${s}`} className="w-20 border border-[#DFDAD0] px-2 py-1.5 text-sm text-center outline-none focus:border-[#1C2530]" />
            <label className="flex items-center justify-center gap-1.5 cursor-pointer w-24">
              <input type="checkbox" checked={cfg.approval_stages.includes(s)} onChange={() => toggleApproval(s)}
                data-testid={`pipeline-cfg-approval-${s}`} className="w-4 h-4 accent-[#E2711D]" />
              <span className="font-mono text-[9px] uppercase text-[#1C2530]/50">req.</span>
            </label>
          </div>
        ))}
      </div>
      <div className="p-4 border-t border-[#DFDAD0] flex items-center justify-between">
        <span className="text-xs text-[#1C2530]/50 max-w-xl">Renaming a column changes only the label — automation (time limits, approvals, reporting) stays keyed to the internal stage. A time limit flags cards overdue in that stage. Checked stages require sign-off before any deal moves into them (Kanban or list).</span>
        <button onClick={save} disabled={busy} data-testid="pipeline-cfg-save"
          className="shrink-0 ml-3 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-sm hover:bg-[#2a3644] disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function LegalReview() {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/tenant/legal-review").then((r) => setSt(r.data)).catch(() => {}); }, []);
  if (!st) return <Loading />;
  if (!st.regulated) return (
    <div className="border border-[#DFDAD0] bg-white p-5 text-sm text-[#1C2530]/60" data-testid="legal-review-none">
      This workspace is not marked as regulated — no legal-review gate is applied.
    </div>
  );
  const clear = async (v) => {
    try { await api.post("/tenant/legal-review/clear", { cleared: v }); setSt({ ...st, cleared: v });
      toast.success(v ? "Legal review cleared" : "Legal review re-armed"); } catch { toast.error("Failed"); }
  };
  return (
    <div className="border border-[#B23A2E]/40 bg-[#B23A2E]/5 p-5 flex items-center justify-between" data-testid="legal-review">
      <div>
        <div className="text-sm font-medium text-[#1C2530]">Regulated workspace</div>
        <div className="text-xs text-[#1C2530]/60 mt-0.5 max-w-md">Automated outreach, Voice Agent calls and client proposal sends are {st.cleared ? "allowed" : "BLOCKED"} until legal review is cleared.</div>
      </div>
      {st.cleared
        ? <button onClick={() => clear(false)} data-testid="legal-rearm-btn" className="border border-[#DFDAD0] px-4 py-2 text-sm rounded-sm hover:bg-[#EFECE5]">Re-arm gate</button>
        : <button onClick={() => clear(true)} data-testid="legal-clear-btn" className="bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-sm rounded-sm hover:bg-[#2a3644]">Clear legal review</button>}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [notif, setNotif] = useState(null);
  const [ext, setExt] = useState(null);
  const [tab, setTab] = useState("workspace");

  useEffect(() => {
    api.get("/users/me/prefs").then((r) => setPrefs(r.data));
    api.get("/users/me/notif-prefs").then((r) => setNotif(r.data));
    if (user.role === "admin") api.get("/settings").then((r) => setExt(r.data.external_calling_enabled));
  }, [user.role]);

  const setApproval = async (k, v) => { setPrefs((p) => ({ ...p, [k]: v })); try { await api.patch("/users/me/prefs", { [k]: v }); toast.success(`${k} → ${v}`); } catch { toast.error("Failed"); } };
  const setNotifPref = async (k, v) => { setNotif((p) => ({ ...p, [k]: v })); try { await api.patch("/users/me/notif-prefs", { [k]: v }); toast.success(k === "digest_frequency" ? `Digest set to ${v.charAt(0).toUpperCase() + v.slice(1)}` : `${k.replace(/_/g, " ")} ${v ? "on" : "muted"}`); } catch { toast.error("Failed"); } };
  const toggleExt = async (v) => { try { const r = await api.patch("/settings/external-calling", { enabled: v }); setExt(r.data.external_calling_enabled); toast.success(`External calling ${v ? "ENABLED" : "disabled"}`); } catch { toast.error("Failed"); } };

  return (
    <>
      <PageHeader eyebrow="PREFERENCES" title="Settings" />
      <div className="p-8 flex flex-col md:flex-row gap-4 md:gap-8 items-start" data-testid="settings-page">
        <nav className="w-full md:w-52 shrink-0 md:sticky md:top-8 flex md:flex-col gap-1 overflow-x-auto hide-scrollbar" data-testid="settings-nav">
          {TAB_DEFS.filter((t) => !t.roles || t.roles.includes(user.role)).map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
              className={`whitespace-nowrap md:w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${tab === t.key ? "bg-[#1C2530] text-[#F5F3EE]" : "text-[#1C2530]/70 hover:bg-[#EFECE5]"}`}>
              {t.label}
            </button>
          ))}
        </nav>
        <div className="flex-1 max-w-3xl space-y-6" data-testid={`settings-panel-${tab}`}>
          {tab === "workspace" && (<>
            {user.role === "admin" && <Group label="Currency & FX rates" desc="Reporting currency for company numbers, currencies you transact in, and the exchange rates used to convert. Rates are snapshotted on every proposal, deal and target at write time — history never changes."><CurrencySettings /></Group>}
            <Group label="Documentation">
              <div className="border border-[#DFDAD0] bg-white p-5 flex items-center justify-between" data-testid="guide-download">
                <div>
                  <div className="text-sm font-medium text-[#1C2530]">Your role guide</div>
                  <div className="text-xs text-[#1C2530]/50 mt-0.5">The live, role-aware guide and FAQs are in Help &amp; Guides — readable in-platform or as a PDF.</div>
                </div>
                <Link to="/help" data-testid="open-help-guides" className="flex items-center gap-1.5 bg-[#E50914] text-white px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#c40810]">
                  <BookOpen className="w-4 h-4" /> Open Help &amp; Guides
                </Link>
              </div>
            </Group>
            {user.role === "admin" && <Group label="Branding · white-label" desc="Set this workspace's logo, colours and login copy. Live preview updates as you edit; saving is blocked until all WCAG AA contrast checks pass."><Branding /></Group>}
            {user.role === "admin" && <Group label="Tenant profile"><TenantProfile /></Group>}
            {(user.role === "admin" || user.role === "supervisor") && <Group label="Do-not-contact list" desc="Addresses that must never receive outreach from Pulse or Jarvis. Admins can remove an entry after verifying consent."><DncList canEdit={user.role === "admin"} /></Group>}
            {user.role === "admin" && (
              <Group label="Onboarding">
                <div className="border border-[#DFDAD0] bg-white p-5 flex items-center justify-between" data-testid="rerun-onboarding">
                  <div><div className="text-sm font-medium text-[#1C2530]">Setup wizard</div><div className="text-xs text-[#1C2530]/50 mt-0.5">Re-run the guided workspace setup any time to review or change your answers.</div></div>
                  <button onClick={() => window.location.assign("/onboarding")} data-testid="rerun-onboarding-btn" className="border border-[#DFDAD0] px-4 py-2 text-sm rounded-sm hover:bg-[#EFECE5]">Re-run setup</button>
                </div>
              </Group>
            )}
          </>)}

          {tab === "pipeline" && (<>
            {user.role === "admin" && <Group label="Pipeline · stage nomenclature & approvals" desc="Rename each pipeline column to your team's terminology and flag which stages require approval before a deal can move into them."><PipelineConfig /></Group>}
            {user.role === "admin" && <Group label="Proposals · owner, format & discount approval ladder" desc="Name who owns Proposal Settings, choose combined vs. split technical/commercial proposals, set the discount threshold and the tiered approval ladder."><ProposalConfig /></Group>}
            <Group label="Automation Approval">
              {!prefs ? <Loading /> : (
                <div className="border border-[#DFDAD0] bg-white divide-y divide-[#DFDAD0]" data-testid="approval-settings">
                  {APPROVAL_ROWS.map((r) => (
                    <div key={r.key} className="flex items-center justify-between p-5">
                      <div><div className="text-sm font-medium text-[#1C2530]">{r.label}</div><div className="text-xs text-[#1C2530]/50 mt-0.5">{r.hint}</div></div>
                      <Toggle value={prefs[r.key] || "draft"} onChange={(v) => setApproval(r.key, v)} testid={`toggle-${r.key}`} />
                    </div>
                  ))}
                </div>
              )}
            </Group>
            {user.role === "admin" && <Group label="Compliance · Legal Review Gate"><LegalReview /></Group>}
          </>)}

          {tab === "email" && (<>
            {user.role === "admin" && <Group label="Mailbox Connect · workspace" desc="Connect this workspace's Gmail and/or Outlook mailbox. Self-service — no platform admin action needed once you finish the consent screen. Sending stays disabled for now."><TenantMailbox /></Group>}
            <Group label="Connected Inbox · email sending"><ConnectedInbox /></Group>
            {user.role === "admin" && <Group label="Sending domain"><SendingDomain /></Group>}
            {user.role === "admin" && <Group label="Email AI Models"><EmailModelPicker /></Group>}
            {user.role === "admin" && <Group label="Email Sending OAuth (Gmail / Outlook)"><EmailOAuthConfig /></Group>}
            {user.role === "admin" && <Group label="Integrations Vault"><IntegrationsVault /></Group>}
          </>)}

          {tab === "reports" && (<>
            {user.role === "admin" && <Group label="Report Distribution"><ReportRecipients /></Group>}
            <Group label="Notification Preferences">
              {!notif ? <Loading /> : (
                <div className="border border-[#DFDAD0] bg-white divide-y divide-[#DFDAD0]" data-testid="notif-settings">
                  {NOTIF_ROWS.map((r) => (
                    <div key={r.key} className="flex items-center justify-between p-5">
                      <div className="text-sm font-medium text-[#1C2530]">{r.label}</div>
                      <OnOff value={notif[r.key] !== false} onChange={(v) => setNotifPref(r.key, v)} testid={`notif-toggle-${r.key}`} />
                    </div>
                  ))}
                  <div className="flex items-center justify-between p-5">
                    <div><div className="text-sm font-medium text-[#1C2530]">Daily digest email</div><div className="text-xs text-[#1C2530]/50 mt-0.5">Your targets-vs-actuals & nurture leads due. Switch to a weekly summary or turn it off.</div></div>
                    <DigestChoice value={notif.digest_frequency || "daily"} onChange={(v) => setNotifPref("digest_frequency", v)} testid="digest-choice" />
                  </div>
                </div>
              )}
            </Group>
          </>)}

          {tab === "security" && (<>
            {(user.role === "admin" || user.role === "supervisor") && <Group label="Account Security"><MfaSection enabled={user.mfa_enabled} /></Group>}
            {user.role === "admin" && (
              <Group label="Voice Agent · External Calling">
                <div className="border border-[#B23A2E]/40 bg-[#B23A2E]/5 p-5 flex items-center justify-between" data-testid="ext-calling-settings">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-[#B23A2E] mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-sm font-medium text-[#1C2530]">Enable external calling</div>
                      <div className="text-xs text-[#1C2530]/60 mt-0.5 max-w-md">OFF by default. While off, the Voice Agent only dials an internal test number. Flipping this is logged to the Change Log (TRAI/DND compliance evidence).</div>
                    </div>
                  </div>
                  {ext === null ? <Loading /> : <OnOff value={ext} onChange={toggleExt} testid="ext-calling-toggle" />}
                </div>
              </Group>
            )}
          </>)}

          {tab === "danger" && user.role === "admin" && (<><DangerZone tenantId={user.tenant_id} /><SyntheticCleanup tenantId={user.tenant_id} /></>)}
        </div>
      </div>
    </>
  );
}
